import { isNativeIOSApp } from 'utils/nativeApp'

type FullscreenDocumentLike = {
    fullscreenElement?: unknown
    webkitFullscreenElement?: unknown
    exitFullscreen?: () => void | Promise<void>
    webkitExitFullscreen?: () => void | Promise<void>
}

type FullscreenTargetLike = {
    contains?: (element: unknown) => boolean
    requestFullscreen?: () => void | Promise<void>
    webkitRequestFullscreen?: () => void | Promise<void>
    webkitRequestFullScreen?: () => void | Promise<void>
}

type VideoElementLike = {
    requestFullscreen?: () => void | Promise<void>
    webkitDisplayingFullscreen?: boolean
    webkitEnterFullscreen?: () => void
    webkitExitFullscreen?: () => void
}

const invokeFullscreenMethod = (
    receiver: object | null | undefined,
    method: (() => void | Promise<void>) | undefined,
) => {
    if (!receiver || typeof method !== 'function') return false

    try {
        method.call(receiver)?.catch?.(() => {})
    } catch {
        return false
    }

    return true
}

const invokeFirstSupportedFullscreenMethod = (
    receiver: object | null | undefined,
    methods: Array<(() => void | Promise<void>) | undefined>,
) => methods.some(method => invokeFullscreenMethod(receiver, method))

const getDocumentFullscreenElement = (fullscreenDocument: FullscreenDocumentLike | null) =>
    fullscreenDocument?.fullscreenElement || fullscreenDocument?.webkitFullscreenElement || null

// Where fixed overlays (upvalue effects, floating amounts) portal to: the
// fullscreen element while one is active, since nothing outside it is drawn.
export const fullscreenPortalHost = (): HTMLElement => {
    const active = getDocumentFullscreenElement(document as FullscreenDocumentLike)
    return active instanceof HTMLElement ? active : document.body
}

const isFullscreenTargetActive = (
    fullscreenElement: unknown,
    fullscreenTarget: FullscreenTargetLike | null | undefined,
) => {
    if (!fullscreenElement || !fullscreenTarget) return false

    const activeElement = fullscreenElement as FullscreenTargetLike
    return (
        fullscreenElement === fullscreenTarget ||
        activeElement.contains?.(fullscreenTarget) === true ||
        fullscreenTarget.contains?.(fullscreenElement) === true
    )
}

// Leaves document fullscreen so a body-level dialog (KnovModal) can be seen.
export const exitDocumentFullscreen = () => {
    if (typeof document === 'undefined') return false
    const fullscreenDocument = document as FullscreenDocumentLike
    if (!getDocumentFullscreenElement(fullscreenDocument)) return false
    return invokeFirstSupportedFullscreenMethod(fullscreenDocument, [
        fullscreenDocument.exitFullscreen,
        fullscreenDocument.webkitExitFullscreen,
    ])
}

export const isVideoFullscreenActive = ({
    fullscreenDocument = typeof document !== 'undefined' ? document : null,
    fullscreenTarget,
    videoElement = null,
}: {
    fullscreenDocument?: FullscreenDocumentLike | null
    fullscreenTarget?: FullscreenTargetLike | null
    videoElement?: VideoElementLike | null
}) =>
    isFullscreenTargetActive(getDocumentFullscreenElement(fullscreenDocument), fullscreenTarget) ||
    videoElement?.webkitDisplayingFullscreen === true

export const toggleVideoFullscreen = ({
    fullscreenDocument = typeof document !== 'undefined' ? document : null,
    fullscreenTarget,
    videoElement = null,
    preferNativeVideoFullscreen = isNativeIOSApp(),
}: {
    fullscreenDocument?: FullscreenDocumentLike | null
    fullscreenTarget?: FullscreenTargetLike | null
    videoElement?: VideoElementLike | null
    preferNativeVideoFullscreen?: boolean
}) => {
    if (!fullscreenDocument) return false

    const fullscreenElement = getDocumentFullscreenElement(fullscreenDocument)
    if (isFullscreenTargetActive(fullscreenElement, fullscreenTarget)) {
        return invokeFirstSupportedFullscreenMethod(fullscreenDocument, [
            fullscreenDocument.exitFullscreen,
            fullscreenDocument.webkitExitFullscreen,
        ])
    }

    if (videoElement?.webkitDisplayingFullscreen) {
        return invokeFullscreenMethod(videoElement, videoElement.webkitExitFullscreen)
    }

    if (preferNativeVideoFullscreen) {
        const requestedNativeVideoFullscreen = invokeFullscreenMethod(
            videoElement,
            videoElement?.webkitEnterFullscreen,
        )
        if (requestedNativeVideoFullscreen) return true
    }

    const requestedFullscreen = invokeFirstSupportedFullscreenMethod(fullscreenTarget, [
        fullscreenTarget?.requestFullscreen,
        fullscreenTarget?.webkitRequestFullscreen,
        fullscreenTarget?.webkitRequestFullScreen,
    ])
    if (requestedFullscreen) return true

    return invokeFullscreenMethod(videoElement, videoElement?.webkitEnterFullscreen)
}
